import { useMemo, useCallback } from 'react';
import { t } from '@lingui/macro';
import styled, { css } from 'styled-components';
import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';

import Search from '@decisiv/iconix/lib/components/Search';

import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import Button from '@decisiv/ui-components/lib/components/Button';
import Plus from '@decisiv/iconix/lib/components/Plus';

import { useCurrentUserContext } from 'utils/User';
import * as ManagementLayout from 'components/ManagementLayout';
import PaginationFooter from 'components/PaginationFooter';
import SearchableFilter from 'components/SearchableFilter';
import EmptyResults from 'components/EmptyResults';
import useFooter from 'utils/useFooter';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

import { SearchProvider } from 'contexts/SearchContext';
import ManagementTeamModalProvider from './CreateTeamModal/contexts/ManagementTeamModalContext';
import ManagementCreateTeamModal from './CreateTeamModal';
import { NoWrapHeaderTable } from '../components/styles';
import { useManagedSentinelTeamsQuery } from './hooks/useManagedSentinelTeamsQuery';

import { useManagementPageContext } from '../contexts/ManagementPageContext';
import useOemSegmentsFilters from '../OemSegments/hooks/useOemSegmentsFilters';
import useTeamsFilters from './hooks/useTeamsFilters';
import { generateColumns } from './columns';
import { ClearAllButton, SearchInputWrapper } from '../styles';
import ToolbarSearch from '../components/ToolbarSearch';
import TOOLBAR_FILTER_PROPS from '../components/toolbarFilterProps';
import useTableUrlState from '../hooks/useTableUrlState';

/**
 * On the route-based page the filters bar sits right under the Teams title
 * from Management.tsx, which has no border of its own; the PD-5930 UX review
 * asked for a light grey line between them.
 */
const FiltersBar = styled(ManagementLayout.TabBody.Content.FiltersBar)<{
  $belowTitle: boolean;
}>`
  ${({ $belowTitle }) =>
    $belowTitle &&
    css`
      border-top: 1px solid ${toColorString(color.base.quarterMoon)};
    `}
`;

export type ManagedTeamsType = ExtractFromArray<
  ManagedSentinelTeamsQuery['managedSentinelTeams']['collection']
>;

const Teams = () => {
  const { footerRef } = useFooter();
  const { isSuperUser, isOemManager, oemSegments } = useCurrentUserContext();

  const fleetAssetManagementEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_5874_FLEET_ASSET_MANAGEMENT,
  );
  const routeBasedManagementEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const amountOfOemSegmentsManaged = oemSegments?.length ?? 0;

  const {
    isFiltersDirty,
    pageParams,
    handleChangeOemSegmentsFilter,
    handleChangeTeamsFilter,
    handleClearFilters,
  } = useManagementPageContext();

  const {
    page,
    setPage,
    apiSort,
    defaultSort,
    onSort,
    liveSearch,
    setLiveSearch,
    debouncedSearch,
  } = useTableUrlState<SentinelTeamsSortColumns>('management-teams');

  const { data, loading } = useManagedSentinelTeamsQuery({
    variables: {
      sort: apiSort,
      page,
      ids: pageParams.teamIds,
      search: debouncedSearch.length > 2 ? debouncedSearch : undefined,
      oemSegmentIds: pageParams.oemSegmentIds,
    },
  });

  const oemSegmentsFilterProps = useOemSegmentsFilters({
    value: pageParams.oemSegmentIds,
    onSubmit: handleChangeOemSegmentsFilter,
  });

  const teamFilterProps = useTeamsFilters({
    value: pageParams.teamIds,
    oemSegmentIds: pageParams.oemSegmentIds,
    onSubmit: handleChangeTeamsFilter,
  });

  const handleSearchTextChange = useCallback(
    ({ target: { value } }) => {
      setLiveSearch(value);
    },
    [setLiveSearch],
  );

  const columns = useMemo(
    () =>
      generateColumns({
        onSort,
        amountOfOemSegmentsManaged,
        isSuperUser,
        fleetAssetManagementEnabled,
      }),
    [
      onSort,
      amountOfOemSegmentsManaged,
      isSuperUser,
      fleetAssetManagementEnabled,
    ],
  );

  const teams = data?.managedSentinelTeams?.collection ?? [];
  const pagination = data?.managedSentinelTeams?.pagination;

  const renderBody = () => (
    <>
      {loading && (
        <Loading
          flex={1}
          justifyContent="center"
          size="large"
          title={t`Loading...`}
        />
      )}
      {!loading && teams.length === 0 && (
        <EmptyResults
          title={t`No Teams Found`}
          subtitle={t`No Managed Teams Found`}
          icon={Search}
        />
      )}
      {!loading && teams.length > 0 && (
        <SearchProvider searchString={debouncedSearch}>
          <NoWrapHeaderTable
            height="100%"
            data={teams}
            scroll={['x', 'y']}
            columns={columns}
            onSort={onSort}
            defaultSort={defaultSort}
            footer={() => (
              <PaginationFooter
                ref={footerRef}
                // @ts-expect-error - JS file
                title={t`Teams`}
                setCurrentPage={setPage}
                totalOnPage={teams?.length}
                page={pagination?.page}
                perPage={pagination?.perPage}
                totalEntries={pagination?.totalEntries}
                totalPages={pagination?.totalPages}
              />
            )}
          />
        </SearchProvider>
      )}
    </>
  );

  const oemSegmentsFilter = amountOfOemSegmentsManaged > 1 && (
    <SearchableFilter
      popoverLabel={t`OEM Segments`}
      placement="bottom-start"
      {...(fleetAssetManagementEnabled ? TOOLBAR_FILTER_PROPS : {})}
      {...oemSegmentsFilterProps}
    />
  );
  const teamsFilter = (
    <SearchableFilter
      popoverLabel={t`Teams`}
      {...(fleetAssetManagementEnabled ? TOOLBAR_FILTER_PROPS : {})}
      {...teamFilterProps}
    />
  );

  // PD-5874 (Figma 686-134159): the search joins the filters in a white
  // toolbar, with Clear All next to the filters. Create Team sits beside the
  // page title, which Management.tsx renders on the route-based pages; the
  // tab-based page has no room there, so it keeps its own header row.
  if (fleetAssetManagementEnabled) {
    return (
      <ManagementLayout.TabBody.Container>
        {!routeBasedManagementEnabled && (isSuperUser || isOemManager) && (
          <ManagementLayout.TabBody.Header.Container>
            <ManagementLayout.TabBody.Header.Left />
            <ManagementLayout.TabBody.Header.Right>
              <Button
                text={t`Create Team`}
                icon={Plus}
                size="small"
                kind="primary"
                disabled
              />
            </ManagementLayout.TabBody.Header.Right>
          </ManagementLayout.TabBody.Header.Container>
        )}
        <ManagementLayout.TabBody.Content.Container>
          <FiltersBar $belowTitle={routeBasedManagementEnabled}>
            <ManagementLayout.TabBody.Content.FilterGroup>
              {oemSegmentsFilter}
              {teamsFilter}
              {isFiltersDirty && (
                <Button
                  variant="ghost"
                  onClick={handleClearFilters}
                  text={t`Clear All`}
                  size="small"
                />
              )}
            </ManagementLayout.TabBody.Content.FilterGroup>
            <ToolbarSearch
              label={t`Search by name, owner email or account ID:`}
              name="searchByTeam"
              value={liveSearch}
              onChange={handleSearchTextChange}
            />
          </FiltersBar>
          {renderBody()}
        </ManagementLayout.TabBody.Content.Container>
      </ManagementLayout.TabBody.Container>
    );
  }

  return (
    <ManagementLayout.TabBody.Container>
      <ManagementLayout.TabBody.Header.Container>
        <ManagementLayout.TabBody.Header.Left>
          {(isSuperUser || isOemManager) && (
            <ManagementTeamModalProvider>
              <ManagementCreateTeamModal />
            </ManagementTeamModalProvider>
          )}
        </ManagementLayout.TabBody.Header.Left>
        <ManagementLayout.TabBody.Header.Right>
          <SearchInputWrapper>
            <SearchInput
              hideLabel
              aria-label={t`Search Teams by Name, Owner Email, or Account ID`}
              helpMessage={t`Search Teams by Name, Owner Email, or Account ID`}
              size="small"
              name="searchByTeam"
              value={liveSearch}
              onChange={handleSearchTextChange}
            />
          </SearchInputWrapper>
        </ManagementLayout.TabBody.Header.Right>
      </ManagementLayout.TabBody.Header.Container>
      <ManagementLayout.TabBody.Content.Container>
        <ManagementLayout.TabBody.Content.Filters>
          {oemSegmentsFilter}
          {teamsFilter}
          {isFiltersDirty && (
            <ClearAllButton
              variant="ghost"
              onClick={handleClearFilters}
              text={t`Clear All`}
              size="small"
            />
          )}
        </ManagementLayout.TabBody.Content.Filters>
        {renderBody()}
      </ManagementLayout.TabBody.Content.Container>
    </ManagementLayout.TabBody.Container>
  );
};

export default Teams;
